/* @jsx h */
import { defineComponent, h, ref } from "vue";
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import { formatValue, type CellFormat } from "../format";

type ColumnSpec = {
  key: string;
  labelKey: string;
  format: CellFormat;
  align?: "left" | "right";
};

type HierarchyDimension = {
  key: string;
  displayKey: string;
  labelKey: string;
  totalKey: string;
  totalLabelKey?: string;
  icon: string;
};

type HierarchyDirection = {
  value: string;
  labelKey: string;
  primary: HierarchyDimension;
  secondary: HierarchyDimension;
  tertiary?: HierarchyDimension;
};

type HierarchySpec = {
  parameterKey: string;
  defaultDirection: string;
  directions: HierarchyDirection[];
};

type TableSpec = {
  rowKey: string;
  columns: ColumnSpec[];
  hierarchy: HierarchySpec;
};

type GroupRow = {
  kind: "group";
  level: "primary" | "secondary";
  key: string;
  label: string;
  total: unknown;
};

type DetailRow = {
  kind: "detail";
  key: string;
  data: Record<string, unknown>;
};

type NestedRow = GroupRow | DetailRow;

type Props = {
  spec: TableSpec;
  rows: Array<Record<string, unknown>>;
  parameters: Record<string, unknown>;
  currency: string;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  onParameterChange: (patch: Record<string, unknown>) => void;
};

/** Responsive expandable table for reversible two- or three-level breakdowns. */
export const NestedReportTable = defineComponent<Props>({
  name: "DataRoomNestedReportTable",
  props: [
    "spec",
    "rows",
    "parameters",
    "currency",
    "loading",
    "loadingMore",
    "hasMore",
    "onLoadMore",
    "onParameterChange",
  ],
  setup(props) {
    const { t } = useLocalization();
    const collapsedGroups = ref<Set<string>>(new Set());

    const toggleGroup = (key: string) => {
      const next = new Set(collapsedGroups.value);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      collapsedGroups.value = next;
    };

    const direction = (): HierarchyDirection => {
      const selected = props.parameters[props.spec.hierarchy.parameterKey];
      return (
        props.spec.hierarchy.directions.find(
          (item) => item.value === selected,
        ) ??
        props.spec.hierarchy.directions.find(
          (item) => item.value === props.spec.hierarchy.defaultDirection,
        ) ??
        props.spec.hierarchy.directions[0]!
      );
    };

    const nestedRows = (active: HierarchyDirection): NestedRow[] => {
      const grouped = new Map<
        string,
        {
          header: GroupRow;
          children: DetailRow[];
          subgroups: Map<string, { header: GroupRow; children: DetailRow[] }>;
        }
      >();

      for (const row of props.rows) {
        const primaryValue = row[active.primary.key];
        const groupKey = `${active.value}:${row.restaurant_id ?? ""}:${
          primaryValue ?? "unattributed"
        }`;
        let group = grouped.get(groupKey);
        if (!group) {
          group = {
            header: {
              kind: "group",
              level: "primary",
              key: groupKey,
              label: String(row[active.primary.displayKey] ?? "—"),
              total: row[active.primary.totalKey],
            },
            children: [],
            subgroups: new Map(),
          };
          grouped.set(groupKey, group);
        }
        const detail: DetailRow = {
          kind: "detail",
          key: `${active.value}:${String(row[props.spec.rowKey])}`,
          data: row,
        };
        if (!active.tertiary) {
          group.children.push(detail);
          continue;
        }

        const secondaryValue = row[active.secondary.key];
        const subgroupKey = `${groupKey}:${secondaryValue ?? "unattributed"}`;
        let subgroup = group.subgroups.get(subgroupKey);
        if (!subgroup) {
          subgroup = {
            header: {
              kind: "group",
              level: "secondary",
              key: subgroupKey,
              label: String(row[active.secondary.displayKey] ?? "—"),
              total: row[active.secondary.totalKey],
            },
            children: [],
          };
          group.subgroups.set(subgroupKey, subgroup);
        }
        subgroup.children.push(detail);
      }

      return [...grouped.values()].flatMap((group) => {
        if (collapsedGroups.value.has(group.header.key)) return [group.header];
        if (!active.tertiary) return [group.header, ...group.children];
        return [
          group.header,
          ...[...group.subgroups.values()].flatMap((subgroup) => [
            subgroup.header,
            ...(collapsedGroups.value.has(subgroup.header.key)
              ? []
              : subgroup.children),
          ]),
        ];
      });
    };

    const columns = (active: HierarchyDirection): ResponsiveTableColumn[] => {
      const dimensionKeys = new Set(
        props.spec.hierarchy.directions.flatMap((item) =>
          [
            item.primary.displayKey,
            item.secondary.displayKey,
            item.tertiary?.displayKey,
          ].filter((key): key is string => key != null),
        ),
      );
      const metrics = props.spec.columns.filter(
        (column) => !dimensionKeys.has(column.key),
      );
      const leaf = active.tertiary ?? active.secondary;

      return [
        {
          key: "nested-dimension",
          label: t(leaf.labelKey),
          icon: leaf.icon,
          mobilePriority: "primary",
          render: (item: DetailRow) => (
            <div
              class={`flex items-center gap-2 font-medium text-base-content ${
                active.tertiary ? "pl-12" : "pl-6"
              }`}
            >
              <span class="material-symbols-outlined text-base text-accent/45">
                {leaf.icon}
              </span>
              <span class="truncate">
                {String(item.data[leaf.displayKey] ?? "—")}
              </span>
            </div>
          ),
        },
        ...metrics.map(
          (column, index): ResponsiveTableColumn => ({
            key: column.key,
            label: t(column.labelKey),
            headerClass: "text-right [&>span]:justify-end",
            cellClass: "text-right tabular-nums",
            mobilePriority: index < 4 ? "secondary" : "hidden",
            render: (item: DetailRow) => {
              const value = item.data[column.key];
              const negative = typeof value === "number" && value < 0;
              return (
                <span class={negative ? "text-error" : ""}>
                  {formatValue(value, column.format, props.currency)}
                </span>
              );
            },
          }),
        ),
      ];
    };

    const renderGroup = (item: NestedRow, active: HierarchyDirection) => {
      if (item.kind !== "group") return null;
      const collapsed = collapsedGroups.value.has(item.key);
      const secondary = item.level === "secondary";
      const dimension = secondary ? active.secondary : active.primary;
      return (
        <button
          type="button"
          class={`flex w-full items-center justify-between gap-3 text-left transition-colors ${
            secondary
              ? "bg-base-200 px-4 py-2.5 pl-10 hover:bg-base-300"
              : "bg-base-300 px-4 py-3 hover:bg-base-300"
          }`}
          aria-expanded={!collapsed}
          onClick={() => toggleGroup(item.key)}
        >
          <span class="flex min-w-0 items-center gap-2.5">
            <span class="material-symbols-outlined text-base text-accent/60">
              {collapsed ? "chevron_right" : "expand_more"}
            </span>
            <span
              class={`grid shrink-0 place-items-center rounded-lg bg-accent/10 text-accent ${
                secondary ? "h-6 w-6" : "h-7 w-7"
              }`}
            >
              <span class="material-symbols-outlined text-base">
                {dimension.icon}
              </span>
            </span>
            <span
              class={`truncate text-accent ${
                secondary ? "font-medium" : "text-[15px] font-bold"
              }`}
            >
              {item.label}
            </span>
          </span>
          <span class="shrink-0 text-right">
            <span class="block text-[10px] font-semibold uppercase tracking-wider text-base-content/70">
              {t(dimension.totalLabelKey ?? "reports.dataroom.group_total")}
            </span>
            <span class="font-bold tabular-nums text-accent">
              {formatValue(item.total, "money", props.currency)}
            </span>
          </span>
        </button>
      );
    };

    return () => {
      const active = direction();
      const rows = nestedRows(active);

      return (
        <div class="overflow-hidden rounded-2xl bg-base-100 shadow-sm">
          <div class="flex flex-wrap items-center justify-between gap-2 bg-base-200 px-3 py-2.5">
            <span class="text-xs font-semibold uppercase tracking-wider text-accent/70">
              {t("reports.dataroom.breakdown")}
            </span>
            <div class="join">
              {props.spec.hierarchy.directions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  class={`btn btn-xs join-item gap-1 normal-case ${
                    item.value === active.value
                      ? "btn-primary"
                      : "bg-base-100 text-base-content hover:border-primary hover:text-primary"
                  }`}
                  onClick={() =>
                    props.onParameterChange({
                      [props.spec.hierarchy.parameterKey]: item.value,
                    })
                  }
                >
                  <span class="material-symbols-outlined text-sm">
                    swap_horiz
                  </span>
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <ResponsiveTable
            columns={columns(active)}
            rows={rows}
            rowKey={(item: NestedRow) => item.key}
            renderFullWidthRow={(item: NestedRow) => renderGroup(item, active)}
            loading={props.loading}
            emptyMessage={t("reports.dataroom.table_empty")}
            stickyHeader={false}
          />

          {props.hasMore && !props.loading && (
            <div class="flex justify-center bg-base-200 p-3">
              <button
                type="button"
                class="btn btn-sm bg-base-100 text-base-content hover:border-primary hover:bg-primary/10 hover:text-primary"
                disabled={props.loadingMore}
                onClick={props.onLoadMore}
              >
                {props.loadingMore ? (
                  <span class="loading loading-spinner loading-xs" />
                ) : (
                  <span class="material-symbols-outlined text-base">
                    expand_more
                  </span>
                )}
                {t("reports.dataroom.table_load_more")}
              </button>
            </div>
          )}
        </div>
      );
    };
  },
});
